import { useCallback, useEffect, useRef, useState } from "react";
import { Award, Backpack, BarChart3, Loader2, ShieldAlert, ShoppingBag, Swords } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { WiredHudMeuPerfil, WiredHudValor, WiredMeuNivel, WiredNivelResgate, wiredService } from "../../services/wired";
import { LojaAtributos } from "./LojaAtributos";
import { Missoes } from "./Missoes";
import { Estatisticas } from "./Estatisticas";
import { NiveisCard } from "./NiveisCard";

const INTERVALO_ATUALIZACAO_MS = 15_000;

const CATEGORIAS_INVENTARIO = [
  ["combate", "Combate"],
  ["medicamentos", "Medicamentos"],
  ["suprimentos", "Suprimentos"],
] as const;

const COR_BARRA: Record<string, string> = {
  vida: "bg-red-400",
  fome: "bg-amber-300",
  hidratacao: "bg-sky-400",
  hidratação: "bg-sky-400",
  estamina: "bg-green-400",
  sanidade: "bg-purple-400",
};

function corDaBarra(rotulo: string, percentual: number | null): string {
  const cor = COR_BARRA[rotulo.trim().toLowerCase()];
  if (cor) return cor;
  if (percentual === null) return "bg-white/30";
  if (percentual < 25) return "bg-red-400";
  if (percentual < 50) return "bg-amber-300";
  return "bg-green-500";
}

function Barra({ variavel }: { variavel: WiredHudValor }) {
  return (
    <div
      className="relative h-5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={variavel.percentual ?? undefined}
    >
      <div
        className={`h-full rounded-full transition-all duration-500 ${corDaBarra(variavel.rotulo, variavel.percentual)}`}
        style={{ width: `${variavel.percentual ?? 0}%` }}
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center px-1 text-xs font-semibold leading-none text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.9)]">
        {variavel.texto}
      </span>
    </div>
  );
}

function Operador({ perfil }: { perfil: WiredHudMeuPerfil }) {
  const avatar =
    `https://www.habbo.com.br/habbo-imaging/avatarimage?user=${encodeURIComponent(perfil.nick)}` +
    "&action=&direction=4&head_direction=2&img_format=png&gesture=sml&headonly=1&size=l";
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[oklch(0.45_0.15_142)] bg-muted">
        <img src={avatar} alt={`Avatar de ${perfil.nick}`} className="h-14 w-14 max-w-none object-contain" referrerPolicy="no-referrer" />
      </div>
      <div className="min-w-0">
        <p className="truncate font-semibold text-foreground">{perfil.nick}</p>
        <p className="truncate text-xs uppercase tracking-wide text-[oklch(0.45_0.15_142)]">
          {perfil.patente ?? "Sem patente"}
        </p>
      </div>
    </div>
  );
}

function Painel({ perfil }: { perfil: WiredHudMeuPerfil }) {
  const variaveis = perfil.variaveis;
  const status = variaveis.filter((v) => v.categoria === "status");
  const outros = variaveis.filter((v) => v.categoria === "outros");
  const inventario = CATEGORIAS_INVENTARIO.map(([categoria, titulo]) => ({
    titulo,
    itens: variaveis.filter((v) => v.categoria === categoria),
  })).filter((grupo) => grupo.itens.length > 0);

  return (
    <div className="space-y-4">
      <Operador perfil={perfil} />

      {perfil.sem_perfil && (
        <p className="rounded-md bg-amber-400/15 px-3 py-2 text-xs text-amber-700 dark:text-amber-200">
          Os valores aparecem quando o jogo registrar suas variáveis.
        </p>
      )}

      {variaveis.length === 0 && (
        <p className="text-sm text-muted-foreground">Nenhuma variável foi configurada para o HUD ainda.</p>
      )}

      {status.length > 0 && (
        <Card>
          <CardContent className="space-y-2 py-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <ShieldAlert className="h-4 w-4" />
              Status
            </h3>
            {status.map((v) => (
              <div key={v.id} className="flex items-center gap-3">
                <span className="w-24 shrink-0 truncate text-xs uppercase tracking-wide text-muted-foreground">
                  {v.rotulo}
                </span>
                {v.formato === "barra" ? (
                  <Barra variavel={v} />
                ) : (
                  <span className="min-w-0 flex-1 truncate text-right text-sm font-semibold text-foreground">
                    {v.texto}
                  </span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {inventario.length > 0 && (
        <Card>
          <CardContent className="space-y-3 py-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Backpack className="h-4 w-4" />
              Inventário
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {inventario.map((grupo) => (
                <div key={grupo.titulo} className="min-w-0 space-y-1.5">
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-[oklch(0.45_0.15_142)]">
                    {grupo.titulo}
                  </h4>
                  {grupo.itens.map((item) => (
                    <div key={item.id} className="rounded border border-border bg-muted/40 px-2 py-1">
                      <span className="block truncate text-xs text-muted-foreground">{item.rotulo}</span>
                      <span className="block text-sm font-semibold text-foreground">{item.texto}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {outros.length > 0 && (
        <Card>
          <CardContent className="space-y-1 py-4">
            {outros.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate text-muted-foreground">{v.rotulo}</span>
                <span className="font-semibold text-foreground">{v.texto}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

type Aba = "ficha" | "niveis" | "loja" | "missoes" | "estatisticas";

function SobreviventeConteudo() {
  const [aba, setAba] = useState<Aba>("ficha");
  const [perfil, setPerfil] = useState<WiredHudMeuPerfil | null>(null);
  const [niveis, setNiveis] = useState<WiredMeuNivel[]>([]);
  const [resgates, setResgates] = useState<WiredNivelResgate[]>([]);
  const [erro, setErro] = useState("");
  const resgatando = useRef(false);

  const buscar = useCallback(async () => {
    const [meuPerfil, meusNiveis] = await Promise.all([
      wiredService.obterHudMeuPerfil(),
      wiredService.obterMeusNiveis().catch(() => ({ niveis: [] as WiredMeuNivel[] })),
    ]);
    setPerfil(meuPerfil);
    setNiveis(meusNiveis.niveis);
    return meusNiveis.niveis;
  }, []);

  const carregar = useCallback(async () => {
    try {
      const atuais = await buscar();
      setErro("");

      // Recompensas de nível são pagas pelo servidor; aqui só pedimos o pagamento.
      if (atuais.some((n) => n.recompensa_pendente) && !resgatando.current) {
        resgatando.current = true;
        try {
          const resultado = await wiredService.resgatarRecompensasNivel();
          if (resultado.resgates.length > 0) {
            setResgates(resultado.resgates);
            await buscar();
          }
        } catch {
          // Tenta de novo na próxima atualização.
        } finally {
          resgatando.current = false;
        }
      }
    } catch (error: any) {
      setErro(error.response?.data?.message || "Não foi possível carregar seus dados.");
    }
  }, [buscar]);

  useEffect(() => {
    carregar();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") carregar();
    }, INTERVALO_ATUALIZACAO_MS);
    return () => window.clearInterval(timer);
  }, [carregar]);

  const classeAba = (nome: Aba) =>
    `flex items-center gap-2 border-b-2 -mb-px px-4 py-2 text-sm font-medium transition-colors ${
      aba === nome
        ? "border-[oklch(0.45_0.15_142)] text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="min-h-screen bg-background p-4 text-foreground">
      <h1 className="mb-3 text-2xl font-bold">Sobrevivente</h1>

      <div className="mb-4 flex gap-1 border-b border-border">
        <button type="button" className={classeAba("ficha")} onClick={() => setAba("ficha")}>
          <ShieldAlert className="h-4 w-4" />
          Status
        </button>
        <button type="button" className={classeAba("niveis")} onClick={() => setAba("niveis")}>
          <Award className="h-4 w-4" />
          Níveis
        </button>
        <button type="button" className={classeAba("loja")} onClick={() => setAba("loja")}>
          <ShoppingBag className="h-4 w-4" />
          Loja
        </button>
        <button type="button" className={classeAba("missoes")} onClick={() => setAba("missoes")}>
          <Swords className="h-4 w-4" />
          Missões
        </button>
        <button type="button" className={classeAba("estatisticas")} onClick={() => setAba("estatisticas")}>
          <BarChart3 className="h-4 w-4" />
          Estatísticas
        </button>
      </div>

      {aba === "ficha" &&
        (perfil ? (
          <div className="space-y-4">
            <Painel perfil={perfil} />
          </div>
        ) : erro ? (
          <p className="text-sm text-red-600">{erro}</p>
        ) : (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ))}

      {aba === "niveis" &&
        (perfil ? (
          <NiveisCard niveis={niveis} resgates={resgates} />
        ) : erro ? (
          <p className="text-sm text-red-600">{erro}</p>
        ) : (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ))}

      {aba === "loja" && <LojaAtributos onComprou={carregar} />}

      {aba === "missoes" && <Missoes onMudou={carregar} />}

      {aba === "estatisticas" && <Estatisticas />}
    </div>
  );
}

/** Só renderiza o conteúdo (e só dispara as chamadas) para quem tem a variável de acesso. */
export function Sobrevivente() {
  const [acesso, setAcesso] = useState<boolean | null>(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    wiredService
      .obterAcessoSobrevivente()
      .then(setAcesso)
      .catch((error: any) => {
        setErro(error.response?.data?.message || "Não foi possível verificar seu acesso.");
        setAcesso(false);
      });
  }, []);

  if (acesso === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!acesso) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4 text-center text-foreground">
        <p className="text-sm text-muted-foreground">{erro || "Você não tem acesso ao Sobrevivente."}</p>
      </div>
    );
  }

  return <SobreviventeConteudo />;
}
